import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FaBuilding,
  FaLayerGroup,
  FaUserFriends,
} from 'react-icons/fa';
import { useUser } from '../../contexts/UserContext';
import { getConsultantDashboardSummary } from './api';

const safeCount = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);

const formatLeadDate = (dateValue) => {
  if (!dateValue) return '—';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const StatCard = ({
  label,
  value,
  icon,
  progressPercent = null,
  progressCaption = null,
  accentColor = 'blue.400',
  actions = [],
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const iconColor = useColorModeValue('gray.700', 'gray.100');

  const showProgress = Number.isFinite(Number(progressPercent));

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      borderLeftWidth="4px"
      borderLeftColor={accentColor}
    >
      <CardBody display="flex" flexDirection="column" gap={3}>
        <HStack justify="space-between" align="start" mb={showProgress ? 3 : 0}>
          <Stat>
            <StatLabel color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
              {label}
            </StatLabel>
            <StatNumber fontSize={{ base: '3xl', md: '4xl' }}>{value ?? '—'}</StatNumber>
          </Stat>
          <Flex
            w="10"
            h="10"
            borderRadius="lg"
            align="center"
            justify="center"
            bg={iconBg}
            color={iconColor}
          >
            <Icon as={icon} boxSize={5} />
          </Flex>
        </HStack>

        {showProgress ? (
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              {progressCaption || `Leads progress: ${Number(progressPercent)}%`}
            </Text>
            <Progress value={Number(progressPercent)} size="sm" borderRadius="full" />
          </Box>
        ) : null}

        {actions.length > 0 ? (
          <HStack mt="auto" justify={{ base: 'stretch', md: 'flex-end' }} spacing={2} flexWrap="wrap">
            {actions.map((action) => (
              <Button
                key={action.label}
                as={RouterLink}
                to={action.to}
                size="sm"
                variant="outline"
                flex={{ base: '1 1 auto', md: '0 0 auto' }}
              >
                {action.label}
              </Button>
            ))}
          </HStack>
        ) : null}
      </CardBody>
    </Card>
  );
};

const ConsultantDashboard = () => {
  const toast = useToast();
  const { user } = useUser();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const welcomeName = user?.first_name?.trim() ? `Welcome back, ${user.first_name.trim()}` : 'Welcome back';
  const consultantName = summary?.consultant?.name?.trim() || 'Your consultant';

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date()),
    []
  );

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const summaryPayload = await getConsultantDashboardSummary();
      setSummary(summaryPayload || null);
    } catch (error) {
      console.error('Error loading consultant dashboard summary:', error);
      toast({
        title: 'Could not load dashboard',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats = useMemo(() => ({
    leads: safeCount(summary?.stats?.leads_total),
    organizations: safeCount(summary?.stats?.organizations_total),
    networks: safeCount(summary?.stats?.networks_total),
    leadsProgressPercent: safeCount(summary?.stats?.leads_progress_percent),
    leadsByStatus: summary?.stats?.leads_by_status || null,
  }), [summary]);

  const leadsCardProgress = useMemo(() => {
    const byStatus = stats?.leadsByStatus;
    if (byStatus && typeof byStatus === 'object') {
      const getCount = (key) => safeCount(byStatus?.[key]) ?? 0;
      const active = getCount('new') + getCount('in_progress') + getCount('booked');
      const converted = getCount('converted');
      const archived = getCount('archived');
      const leadsTotal = safeCount(stats?.leads) ?? 0;
      const eligible = Math.max(1, leadsTotal - archived);
      const percent = Math.max(0, Math.min(100, Math.round((converted / eligible) * 100)));

      return {
        percent,
        caption: `Converted ${converted} of ${eligible} active`,
        active,
      };
    }

    if (Number.isFinite(Number(stats?.leadsProgressPercent))) {
      return {
        percent: Number(stats.leadsProgressPercent),
        caption: `Leads progress: ${Number(stats.leadsProgressPercent)}%`,
      };
    }

    return null;
  }, [stats]);

  const organizationsPreview = useMemo(
    () => (Array.isArray(summary?.organizations_preview) ? summary.organizations_preview.slice(0, 5) : []),
    [summary]
  );

  const recentLeads = useMemo(
    () => (Array.isArray(summary?.recent_leads) ? summary.recent_leads.slice(0, 5) : []),
    [summary]
  );

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} alignItems="center">
            <Box>
              <Heading size="md">{welcomeName}</Heading>
              <Text color={mutedColor} fontSize="sm" mt={1}>{consultantName}</Text>
            </Box>
            <Box />
            <HStack justify={{ base: 'start', md: 'end' }}>
              <Badge colorScheme="blue" borderRadius="full" px={3} py={1} fontSize="0.75rem">
                {today}
              </Badge>
            </HStack>
          </SimpleGrid>
        </CardBody>
      </Card>

      {isLoading ? (
        <HStack justify="center" py={12}><Spinner size="lg" /></HStack>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} spacing={4} mb={6}>
            <StatCard
              label="Leads"
              value={stats.leads}
              icon={FaUserFriends}
              progressPercent={leadsCardProgress?.percent}
              progressCaption={leadsCardProgress?.caption}
              accentColor="teal.400"
              actions={[
                { label: 'View leads', to: '/app/consultant/leads' },
              ]}
            />
            <StatCard
              label="Orgs"
              value={stats.organizations}
              icon={FaBuilding}
              accentColor="orange.400"
              actions={[
                { label: 'View schools', to: '/app/consultant/organizations' },
              ]}
            />
            <StatCard
              label="Networks"
              value={stats.networks}
              icon={FaLayerGroup}
              accentColor="purple.400"
              actions={[
                { label: 'View networks', to: '/app/consultant/networks' },
              ]}
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
              <CardBody>
                <HStack justify="space-between" mb={3}>
                  <Heading size="sm">Schools</Heading>
                  <Button as={RouterLink} size="sm" variant="outline" to="/app/consultant/organizations">
                    View all schools
                  </Button>
                </HStack>

                {organizationsPreview.length === 0 ? (
                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" px={3} py={3}>
                    <Text fontSize="sm" color={mutedColor}>
                      No schools yet. They will show up here once available.
                    </Text>
                    <Button
                      as={RouterLink}
                      to="/app/consultant/organizations"
                      mt={3}
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                    >
                      Create a School
                    </Button>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <Thead>
                        <Tr>
                          <Th>Name</Th>
                          <Th>Location</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {organizationsPreview.map((organization, index) => {
                          const city = organization?.city?.trim() || '';
                          const country = organization?.country?.trim() || '';
                          const location = city && country ? `${city}, ${country}` : city || country || '—';

                          return (
                            <Tr key={organization?.id || `${organization?.name || 'organization'}-${index}`}>
                              <Td fontWeight="semibold">{organization?.name?.trim() || '—'}</Td>
                              <Td>{location}</Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </CardBody>
            </Card>

            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl">
              <CardBody>
                <Heading size="sm" mb={3}>Recent leads</Heading>

                {recentLeads.length === 0 ? (
                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" px={3} py={3}>
                    <Text fontSize="sm" color={mutedColor}>
                      No recent leads yet. New leads will appear here when they arrive.
                    </Text>
                    <Button
                      as={RouterLink}
                      to="/app/consultant/leads"
                      mt={3}
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                    >
                      Go to Leads
                    </Button>
                  </Box>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {recentLeads.map((lead, index) => (
                      <HStack
                        key={lead?.id || `${lead?.email || 'lead'}-${index}`}
                        justify="space-between"
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        px={3}
                        py={2}
                      >
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold">{lead?.name || lead?.email || 'Unnamed lead'}</Text>
                          <Text fontSize="xs" color={mutedColor}>{lead?.organization_name || '—'}</Text>
                        </Box>
                        <Text fontSize="xs" color={mutedColor}>{formatLeadDate(lead?.created_at)}</Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </CardBody>
            </Card>
          </SimpleGrid>
        </>
      )}
    </Box>
  );
};

export default ConsultantDashboard;
